import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calculator, DollarSign, Lock, Unlock, Printer, X, Search } from "lucide-react";
import { format } from "date-fns";

export default function ViewRegister() {
  const { getAuthHeader, user } = useAuth();
  const [currentRegister, setCurrentRegister] = useState(null);
  const [registerHistory, setRegisterHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("100.00");
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");

  useEffect(() => {
    fetchRegisterData();
  }, []);

  const fetchRegisterData = async () => {
    try {
      const [currentRes, historyRes] = await Promise.all([
        axios.get(`${API}/register/current`, getAuthHeader()),
        axios.get(`${API}/register/history`, getAuthHeader())
      ]);
      setCurrentRegister(currentRes.data);
      setRegisterHistory(historyRes.data);
    } catch (error) {
      console.error("Failed to fetch register data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    try {
      await axios.post(
        `${API}/register/open`,
        { opening_balance: parseFloat(openingBalance) },
        getAuthHeader()
      );
      toast.success("Register opened successfully!");
      setShowOpenModal(false);
      fetchRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open register");
    }
  };

  const handleCloseRegister = async (registerId) => {
    try {
      await axios.post(`${API}/register/close`, {}, getAuthHeader());
      toast.success("Register closed successfully!");
      fetchRegisterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to close register");
    }
  };

  const handlePrintRegister = (register) => {
    const printContent = `
      REGISTER SUMMARY
      ================
      POS: ${register.pos_name}
      Cashier: ${register.username}
      Date: ${format(new Date(register.created_at), "MMM dd, yyyy")}
      
      Opening Balance: $${register.opening_balance.toFixed(2)}
      Total Sales: ${register.total_sales}
      Sales Amount: $${register.total_sales_amount.toFixed(2)}
      Closing Balance: $${register.closing_balance.toFixed(2)}
      Status: ${register.status}
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<pre style="font-family: monospace;">${printContent}</pre>`);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="view-register-page">
      {/* Header with Actions */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Today's Register</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">From:</span>
            <Input
              type="time"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
              className="w-28 h-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">To:</span>
            <Input
              type="time"
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
              className="w-28 h-9"
            />
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
          <Button
            variant="destructive"
            className="rounded-full w-12 h-12"
            onClick={() => window.history.back()}
          >
            Close
          </Button>
        </div>
      </div>

      {/* Register History Table */}
      <Card>
        <CardContent className="p-0">
          {registerHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Calculator className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No register history found</p>
              <Button
                onClick={() => setShowOpenModal(true)}
                className="mt-4 bg-green-600 hover:bg-green-700"
              >
                <Unlock className="w-4 h-4 mr-2" />
                Open Register
              </Button>
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="cart-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>POS Name</th>
                    <th>User Name</th>
                    <th>Opening Balance</th>
                    <th>Closing Balance</th>
                    <th>Created On</th>
                    <th>Updated On</th>
                    <th>Total Sales</th>
                    <th>Total Sale Amount</th>
                    <th>Register Status</th>
                    <th>Close Register</th>
                    <th>Print</th>
                  </tr>
                </thead>
                <tbody>
                  {registerHistory.map((reg, index) => (
                    <tr key={reg.id}>
                      <td className="font-mono">{index + 1}</td>
                      <td>Warehouse</td>
                      <td>{reg.username}</td>
                      <td className="font-mono">{reg.opening_balance.toFixed(2)}</td>
                      <td className="font-mono">{reg.closing_balance.toFixed(2)}</td>
                      <td>{format(new Date(reg.created_at), "MMM dd, yyyy")}</td>
                      <td>{format(new Date(reg.updated_at), "MMM dd, yyyy")}</td>
                      <td className="font-mono font-bold">{reg.total_sales}</td>
                      <td className="font-mono font-bold text-green-600">
                        {reg.total_sales_amount.toFixed(2)}
                      </td>
                      <td>
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          reg.status === "Open" 
                            ? "bg-green-100 text-green-700" 
                            : "bg-red-100 text-red-700"
                        }`}>
                          {reg.status}
                        </span>
                      </td>
                      <td>
                        {reg.status === "Open" ? (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleCloseRegister(reg.id)}
                            data-testid={`close-register-${reg.id}`}
                          >
                            Close Register
                          </Button>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-slate-500 hover:bg-slate-600 text-white border-0"
                          onClick={() => handlePrintRegister(reg)}
                          data-testid={`print-register-${reg.id}`}
                        >
                          Print
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Open Register Modal */}
      <Dialog open={showOpenModal} onOpenChange={setShowOpenModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Open Register</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Opening Balance (Cash in drawer)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  className="pl-10 h-12 text-xl font-mono"
                  data-testid="opening-balance-input"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowOpenModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleOpenRegister} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-open-register-btn"
              >
                Open Register
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
